import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { apiRequest } from '@/lib/queryClient';
import { useMutation } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { FileCheck, AlertTriangle, FileText } from 'lucide-react';

export default function PDFUploadTest() {
  const [file, setFile] = useState<File | null>(null);
  const [fileData, setFileData] = useState<{
    data: string;
    name: string;
    type: string;
    size: number;
    isPDFValid?: boolean;
  } | null>(null);
  const [validationMessage, setValidationMessage] = useState<string | null>(null);
  const { toast } = useToast();

  // Function to check if a file appears to be a valid PDF (basic check)
  const validatePDF = (dataUrl: string): { isValid: boolean; message: string } => {
    // Check if it has the correct content type
    if (!dataUrl.startsWith('data:application/pdf')) {
      return {
        isValid: false,
        message: 'File does not have PDF content type',
      };
    }

    // Extract the base64 data
    const base64Data = dataUrl.split(';base64,')[1];
    if (!base64Data) {
      return {
        isValid: false,
        message: 'Invalid data URL format',
      };
    }

    // Check minimum viable PDF size (a valid PDF should be at least a few hundred bytes)
    if (base64Data.length < 100) {
      return {
        isValid: false,
        message: 'File appears to be too small to be a valid PDF',
      };
    }

    // Decode the start of the base64 data to check for PDF signature (%PDF-)
    try {
      const decodedStart = atob(base64Data.substring(0, 100));
      if (!decodedStart.includes('%PDF-')) {
        return {
          isValid: false,
          message: 'File does not contain PDF signature',
        };
      }
    } catch (e) {
      return {
        isValid: false,
        message: 'Could not decode file data for validation',
      };
    }

    return {
      isValid: true,
      message: 'PDF validation passed',
    };
  };

  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0] || null;
    if (!selectedFile) {
      setFile(null);
      setFileData(null);
      setValidationMessage(null);
      return;
    }

    setFile(selectedFile);
    setValidationMessage(null);

    // Check file type before reading
    if (selectedFile.type !== 'application/pdf') {
      setValidationMessage('Selected file is not a PDF. Please select a PDF file.');
      return;
    }

    // Read file as data URL
    const reader = new FileReader();

    reader.onload = () => {
      if (typeof reader.result === 'string') {
        const result = reader.result;
        console.log('File loaded as data URL:', {
          name: selectedFile.name,
          type: selectedFile.type,
          size: selectedFile.size,
          dataURLLength: result.length,
        });

        // Validate the PDF
        const validation = validatePDF(result);

        // Store file data with metadata and validation result
        setFileData({
          data: result, // Store original data URL
          name: selectedFile.name,
          type: selectedFile.type,
          size: selectedFile.size,
          isPDFValid: validation.isValid,
        });

        setValidationMessage(validation.message);

        if (!validation.isValid) {
          toast({
            title: 'PDF Validation Warning',
            description: validation.message,
            variant: 'destructive',
          });
        }

        console.log('Data URL stored (length):', result.length);
        console.log('PDF validation result:', validation);
      }
    };

    reader.onerror = () => {
      setValidationMessage('Error reading file. Please try again.');
      toast({
        title: 'File Read Error',
        description: 'Could not read the selected file',
        variant: 'destructive',
      });
    };

    reader.readAsDataURL(selectedFile);
  };

  // Enhance the data URL with proper metadata
  const enhanceDataUrl = (dataUrl: string, filename: string): string => {
    // Check if the data URL already includes filename metadata
    if (dataUrl.includes(';filename=')) {
      return dataUrl;
    }

    // Extract parts
    const parts = dataUrl.split(';base64,');
    if (parts.length !== 2) {
      return dataUrl; // Can't enhance if format is unexpected
    }

    // Add filename metadata
    return `${parts[0]};filename=${encodeURIComponent(filename)};base64,${parts[1]}`;
  };

  // Test installation PATCH mutation
  const testUploadMutation = useMutation({
    mutationFn: async () => {
      if (!fileData) {
        throw new Error('No file selected');
      }

      // Enhance data URL with filename metadata before sending
      const enhancedDataUrl = enhanceDataUrl(fileData.data, fileData.name);

      // Using our special test endpoint that's dedicated to testing PDF uploads
      const testPayload = {
        installationId: 4,
        pdfData: enhancedDataUrl,
      };

      console.log('Sending PDF data to test endpoint for file:', fileData.name);
      console.log('Enhanced data URL with metadata for better PDF handling');

      return await apiRequest({
        method: 'POST',
        url: `/api/test/pdf-upload`,
        data: testPayload,
      });
    },
    onSuccess: (data) => {
      console.log('Upload test completed successfully:', data);
      toast({
        title: 'Test upload successful',
        description: `The PDF "${fileData?.name}" was successfully uploaded to installation #4`,
      });
    },
    onError: (error: Error) => {
      console.error('Upload test failed:', error);
      toast({
        title: 'Test upload failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle>PDF Upload Test</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <input
            type="file"
            accept="application/pdf"
            onChange={handleFileChange}
            className="mb-4"
          />

          {validationMessage && (
            <Alert
              className={`mb-4 ${fileData?.isPDFValid ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}
            >
              <div className="flex items-center gap-2">
                {fileData?.isPDFValid ? (
                  <FileCheck className="h-4 w-4 text-green-600" />
                ) : (
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                )}
                <AlertTitle>
                  {fileData?.isPDFValid ? 'PDF Validation Passed' : 'PDF Validation Warning'}
                </AlertTitle>
              </div>
              <AlertDescription className="mt-2 text-sm">{validationMessage}</AlertDescription>
            </Alert>
          )}

          {file && (
            <div className="bg-secondary p-4 rounded-md">
              <p>
                <strong>Selected file:</strong> {file.name}
              </p>
              <p>
                <strong>Type:</strong> {file.type}
              </p>
              <p>
                <strong>Size:</strong> {formatBytes(file.size)}
              </p>
            </div>
          )}
        </div>
      </CardContent>
      <CardFooter>
        <Button
          onClick={() => testUploadMutation.mutate()}
          disabled={!fileData || testUploadMutation.isPending}
        >
          {testUploadMutation.isPending ? 'Uploading...' : 'Test Upload'}
        </Button>
      </CardFooter>
    </Card>
  );
}

// Helper function to format bytes
function formatBytes(bytes: number, decimals = 2): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(decimals)) + ' ' + sizes[i];
}
